/**
 * Phase 5 PR 5.1 — weekday-convention helpers.
 *
 * Why this module exists
 *
 * The DB and JS disagree on which day starts the week:
 *
 *   - `plan_days.day_of_week` is **Monday-based**: 0=Mon, 1=Tue, …, 6=Sun.
 *     Inherited from `dayLabelToIndex` in `api/lib/plan-parse.js` which
 *     uses `DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']`,
 *     and the persist code at `api/index.js:3107` (now ~3127 post-PR-5.0)
 *     stores the value as-is. Verified empirically against 44 plans
 *     (308 rows) on 2026-05-01.
 *
 *   - JavaScript `Date.getDay()` is **Sunday-based**: 0=Sun, 1=Mon, …, 6=Sat.
 *
 * Comparing the two directly is off-by-one for Mon–Sat and *completely
 * wrong* for Sun (would compare 6 vs 0). Every Phase 5 hook that touches
 * `plan_days.day_of_week` MUST go through this module.
 *
 * The conversion: `(jsDay + 6) % 7` rotates Sunday from 0 to 6 and shifts
 * everything else down by one — Mon(1)→0, Tue(2)→1, …, Sat(6)→5.
 *
 * All helpers operate in the user's **local timezone**. Switching to
 * UTC here would break "today's workout" on the day boundary
 * (post-midnight local would still show yesterday's plan).
 */

/**
 * The current day-of-week in **Monday-based, local-timezone** form.
 *
 * Returns 0 (Mon) .. 6 (Sun). Use this when comparing to
 * `plan_days.day_of_week`.
 */
export function todayMondayBasedLocal(now: Date = new Date()): number {
  return (now.getDay() + 6) % 7;
}

/**
 * Convert a JS `Date.getDay()` value (Sunday=0) to Monday-based (Monday=0).
 * Pure helper — exposed so the conversion is grep-able from anywhere.
 */
export function toMondayBased(jsDay: number): number {
  return ((jsDay % 7) + 7 + 6) % 7; // defensive against negatives
}

/**
 * Returns 7 dates representing the current calendar week's Monday
 * through Sunday in **local timezone**, with all times set to 00:00:00
 * local. Index 0 = this week's Monday, index 6 = this week's Sunday.
 *
 * Used to render `dayNumber` (calendar day-of-month) on the WeekPlan
 * grid, matching the existing `ramiroWeek` fixture shape.
 *
 * Edge case: if `now` is Sunday, the returned Monday is the *previous*
 * Monday (this week's Mon), not next Monday. Confirmed correct because
 * the dashboard's "this week" is the week containing today.
 */
export function currentWeekDates(now: Date = new Date()): Date[] {
  const mondayOffset = todayMondayBasedLocal(now); // 0 if Mon, 6 if Sun
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - mondayOffset);
  return [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

/**
 * The Monday-based day-label order used by `plan_days.day_label`.
 * Mirrors `DAY_LABELS` in `api/lib/plan-parse.js`.
 *
 * Index === Monday-based day_of_week, so:
 *   DAY_LABELS_MON_BASED[0] === 'Mon'
 *   DAY_LABELS_MON_BASED[6] === 'Sun'
 */
export const DAY_LABELS_MON_BASED = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const;

export type DayLabelMonBased = (typeof DAY_LABELS_MON_BASED)[number];

/**
 * Uppercase short labels matching the existing `Day.dayShort` shape on
 * `WeeklyPlan`. Used by adapters that hydrate the WeekPlan grid from DB.
 */
export const DAY_SHORT_UPPER = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
] as const;

export type DayShortUpper = (typeof DAY_SHORT_UPPER)[number];
